import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi, money } from '../../lib/api';

export default function InvoiceNew() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [customerId, setCustomerId] = useState('');
  const [items, setItems] = useState([{ product_id: '', name: '', qty: 1, unit_price: 0 }]);
  const [dueDate, setDueDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 30); return d.toISOString().slice(0, 10);
  });
  const [notes, setNotes] = useState('');
  const [customPricing, setCustomPricing] = useState({});

  useEffect(() => {
    adminApi.get('/admin/customers').then((r) => setCustomers(r.data));
    adminApi.get('/admin/products').then((r) => setProducts(r.data));
  }, []);

  useEffect(() => {
    if (customerId) {
      adminApi.get(`/admin/customers/${customerId}`).then((r) => {
        const map = {};
        (r.data.pricing || []).forEach((p) => { map[p.product_id] = p.price; });
        setCustomPricing(map);
      });
    }
  }, [customerId]);

  const updateItem = (i, patch) => {
    const next = [...items];
    next[i] = { ...next[i], ...patch };
    // Auto-fill price & name when product selected
    if (patch.product_id) {
      const prod = products.find((p) => p.product_id === patch.product_id);
      if (prod) {
        next[i].name = prod.name;
        next[i].unit_price = customPricing[prod.product_id] ?? prod.default_price;
      }
    }
    setItems(next);
  };
  const addLine = () => setItems([...items, { product_id: '', name: '', qty: 1, unit_price: 0 }]);
  const removeLine = (i) => setItems(items.filter((_, idx) => idx !== i));

  const subtotal = items.reduce((s, i) => s + i.qty * i.unit_price, 0);

  const save = async (thenConfirm = false) => {
    if (!customerId) return toast.error('Select a customer');
    if (items.length === 0 || items.some((i) => !i.name || i.qty <= 0)) return toast.error('Add at least one valid line item');
    try {
      const body = {
        customer_id: customerId,
        items: items.map((i) => ({ product_id: i.product_id || '', name: i.name, qty: Number(i.qty), unit_price: Number(i.unit_price) })),
        due_date: new Date(dueDate).toISOString(),
        notes,
      };
      const r = await adminApi.post('/admin/invoices', body);
      toast.success('Invoice created');
      if (thenConfirm) {
        await adminApi.post(`/admin/invoices/${r.data.invoice_id}/confirm`);
        toast.success('Invoice confirmed & emailed');
      }
      navigate(`/admin/orders/${r.data.invoice_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create invoice');
    }
  };

  return (
    <div className="fade-in space-y-6" data-testid="invoice-new-page">
      <button onClick={() => navigate('/admin/orders')} className="text-sm text-[#6b7280] hover:text-[#0a0a0a] flex items-center gap-1">
        <ArrowLeft size={16} /> Back to invoices
      </button>
      <h1 className="text-3xl font-semibold">New Invoice</h1>

      <div className="card p-5 sm:p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="block">
            <div className="text-xs font-semibold text-[#1f1f1f] uppercase tracking-wide mb-1">Customer</div>
            <select value={customerId} onChange={(e) => setCustomerId(e.target.value)} className="w-full px-3 py-2.5 border border-[#e7e7e7] rounded-lg text-sm" data-testid="customer-select">
              <option value="">Select customer...</option>
              {customers.map((c) => <option key={c.customer_id} value={c.customer_id}>{c.name} — {c.company || c.email}</option>)}
            </select>
          </label>
          <label className="block">
            <div className="text-xs font-semibold text-[#1f1f1f] uppercase tracking-wide mb-1">Due Date</div>
            <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="w-full px-3 py-2.5 border border-[#e7e7e7] rounded-lg text-sm" data-testid="due-date-input" />
          </label>
        </div>
      </div>

      <div className="card p-5 sm:p-6 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold">Line Items</h2>
          <button onClick={addLine} className="btn-ghost text-sm flex items-center gap-1" data-testid="add-line-item"><Plus size={14} /> Add line</button>
        </div>
        <div className="space-y-2">
          {items.map((it, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-end">
              <div className="col-span-12 md:col-span-5">
                <label className="text-[10px] text-[#6b7280] uppercase block mb-1">Product</label>
                <select value={it.product_id} onChange={(e) => updateItem(i, { product_id: e.target.value })} className="w-full px-3 py-2 border border-[#e7e7e7] rounded-lg text-sm" data-testid={`line-product-${i}`}>
                  <option value="">— Custom / Delivery —</option>
                  {products.map((p) => <option key={p.product_id} value={p.product_id}>{p.name}</option>)}
                </select>
                {!it.product_id && (
                  <input type="text" value={it.name} onChange={(e) => updateItem(i, { name: e.target.value })} placeholder="Description" className="mt-1 w-full px-3 py-2 border border-[#e7e7e7] rounded-lg text-sm" data-testid={`line-name-${i}`} />
                )}
              </div>
              <div className="col-span-3 md:col-span-2">
                <label className="text-[10px] text-[#6b7280] uppercase block mb-1">Qty</label>
                <input type="number" min="1" value={it.qty} onChange={(e) => updateItem(i, { qty: Number(e.target.value) })} className="w-full px-3 py-2 border border-[#e7e7e7] rounded-lg text-sm mono" data-testid={`line-qty-${i}`} />
              </div>
              <div className="col-span-4 md:col-span-2">
                <label className="text-[10px] text-[#6b7280] uppercase block mb-1">Unit Price</label>
                <input type="number" step="0.01" value={it.unit_price} onChange={(e) => updateItem(i, { unit_price: Number(e.target.value) })} className="w-full px-3 py-2 border border-[#e7e7e7] rounded-lg text-sm mono" data-testid={`line-price-${i}`} />
              </div>
              <div className="col-span-4 md:col-span-2 text-right">
                <label className="text-[10px] text-[#6b7280] uppercase block mb-1">Total</label>
                <div className="px-3 py-2 mono font-semibold">{money(it.qty * it.unit_price)}</div>
              </div>
              <div className="col-span-1">
                <button onClick={() => removeLine(i)} className="p-2 text-[#d92027] hover:bg-[#fde8e8] rounded" data-testid={`remove-line-${i}`}><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
        <div className="pt-4 border-t border-[#f3f3f3] flex justify-end">
          <div className="text-right">
            <div className="text-xs text-[#6b7280] uppercase tracking-wide">Subtotal</div>
            <div className="text-3xl font-semibold mono text-[#d92027]">{money(subtotal)}</div>
          </div>
        </div>
      </div>

      <div className="card p-5 sm:p-6">
        <label className="block">
          <div className="text-xs font-semibold text-[#1f1f1f] uppercase tracking-wide mb-1">Notes</div>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="w-full px-3 py-2.5 border border-[#e7e7e7] rounded-lg text-sm" placeholder="Optional notes..." data-testid="invoice-notes" />
        </label>
      </div>

      <div className="flex gap-2 flex-wrap justify-end">
        <button onClick={() => save(false)} className="btn-ghost" data-testid="save-draft-btn">Save as Draft</button>
        <button onClick={() => save(true)} className="btn-brand" data-testid="create-send-btn">Create & Send Invoice</button>
      </div>
    </div>
  );
}
